import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FieldBinaryOutlined, EllipsisOutlined, ExclamationCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Table, Typography } from 'antd';
import { apiClient, apiErrorMessage, hasPermission } from '../../../utils';
import { ErrorCode } from '../../../models';
import { UserContext } from '../../contexts';
import { ErrorCodeEditPage } from './error-code-edit-page';
import { EntityChangeLog } from '../../reports';

export function ErrorCodesPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [errorCodes, setErrorCodes] = useState<ErrorCode[]>();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalErrorCode, setEditModalErrorCode] = useState<ErrorCode>();
  const [canEditAll, setCanEditAll] = useState(false);
  const [changeLogModalOpen, setChangeLogModalOpen] = useState<string>();

  useEffect(() => {
    setCanEditAll(hasPermission(user, 'errorCodes.edit'));
  }, [user]);

  const loadErrorCodes = useCallback(() => {
    setLoading(true);
    apiClient
      .get(`error-codes`)
      .then((response: any) => {
        setErrorCodes(response.data);
        setLoading(false);
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadErrorCodes();
  }, [loadErrorCodes]);

  const onRefreshClick = () => {
    loadErrorCodes();
  };

  const onErrorCodeEditSaved = () => {
    closeEditModal();
    loadErrorCodes();
  };

  const openEditModal = () => {
    setEditModalErrorCode(undefined);
    setEditModalOpen(true);
  };

  const openEditModalByErrorCodes = (errorCode: ErrorCode) => {
    setEditModalErrorCode(errorCode);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditModalErrorCode(undefined);
  };

  const deleteErrorCode = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`error-codes/${id}`)
      .then(() => {
        setLoading(false);
        loadErrorCodes();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const confirmDeleteErrorCode = (id: string) => {
    const onOk = () => deleteErrorCode(id);
    Modal.confirm({
      title: 'Hata kodu sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Hata kodu silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const openChangeLogModal = (id: string) => {
    setChangeLogModalOpen(id);
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'changelog') {
      openChangeLogModal(id);
    }
    if (e.key === 'edit') {
      const errorCode = errorCodes?.find(m => m.id === id);
      if (errorCode) {
        openEditModalByErrorCodes(errorCode);
      } else {
        openEditModal();
      }
    } else if (e.key === 'del') {
      confirmDeleteErrorCode(id);
    }
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const tableActions = (id: string) => (
    <Menu onClick={(e: any) => onTableActionClick(e, id)}>
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
      {canEditAll && (
        <>
          <Menu.Item key="edit">
            <Text>Düzenle</Text>
          </Menu.Item>
          <Menu.Item key="del">
            <Text type="danger">Sil</Text>
          </Menu.Item>
        </>
      )}
    </Menu>
  );

  const tableColumns: any[] = [
    {
      title: 'İsim',
      dataIndex: 'code',
      key: 'code',
      render: (name: string, errorCode: ErrorCode) => (
        <Button type="link" onClick={() => openEditModalByErrorCodes(errorCode)}>
          {name}
        </Button>
      ),
    },
    {
      title: 'Hata tanımları',
      dataIndex: ['description'],
      key: 'description',
    },
    {
      title: 'Birim fiyatı (€)',
      dataIndex: ['unitPrice'],
      key: 'unitPrice',
    },
    {
      title: '',
      key: 'action',
      render: (_: any, errorCode: ErrorCode) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(errorCode.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <FieldBinaryOutlined /> Hata kodları
          </>
        }
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <Button key="add" onClick={openEditModal} icon={<PlusOutlined />} type="primary">
            Yeni hata kodu
          </Button>,
        ]}
      />
      <Content>
        <Table columns={tableColumns} dataSource={errorCodes} loading={loading} pagination={false} />
      </Content>
      <Modal
        title="Değişim geçmişi"
        visible={changeLogModalOpen !== undefined}
        footer={null}
        onCancel={closeChangeLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <EntityChangeLog entityName={'ErrorCode'} recordId={changeLogModalOpen} />
      </Modal>
      <Modal title="Hata kodu" visible={editModalOpen} footer={null} onCancel={closeEditModal} destroyOnClose={true}>
        <ErrorCodeEditPage errorCode={editModalErrorCode} onCancel={closeEditModal} onSaved={onErrorCodeEditSaved} />
      </Modal>
    </>
  );
}
